/**
 * Liste « Mes découvertes » — /discovery/events
 * Affiche : événements recommandés et/ou sauvegardés.
 * Onglets : Recommandés / Sauvegardés
 */

import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Button, CircularProgress, Alert } from '@mui/material'
import './Discovery.css'
import { getDiscoveryEvents } from '../../utils/discoveryApi'

const EventsList = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tab, setTab] = useState(searchParams.get('saved') === '1' ? 'saved' : 'recommended')

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true)
        const params = tab === 'saved' ? { saved: 1 } : {}
        const res = await getDiscoveryEvents(params)
        if (res?.status === 200 && res.data) {
          setEvents(res.data.results || [])
        } else {
          setError(t('discovery.errorLoading'))
        }
      } catch (err) {
        console.error('Failed to load events:', err)
        setError(t('discovery.errorLoading'))
      } finally {
        setLoading(false)
      }
    }
    fetchEvents()
  }, [tab, t])

  const handleTabChange = (newTab) => {
    setTab(newTab)
    if (newTab === 'saved') {
      navigate('?saved=1')
    } else {
      navigate('/discovery/events')
    }
  }

  if (loading) {
    return (
      <Box className="dsc-page">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box className="dsc-page dsc-events-list">
      <Box className="dsc-header">
        <h1>{t('discovery.myDiscoveries')}</h1>
      </Box>

      <Box className="dsc-tabs">
        <button
          className={`dsc-tab ${tab === 'recommended' ? 'dsc-tab-active' : ''}`}
          onClick={() => handleTabChange('recommended')}
        >
          {t('discovery.recommended')}
        </button>
        <button
          className={`dsc-tab ${tab === 'saved' ? 'dsc-tab-active' : ''}`}
          onClick={() => handleTabChange('saved')}
        >
          {t('discovery.saved')}
        </button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {events.length === 0 ? (
        <Box className="dsc-empty">
          <h2>
            {tab === 'saved'
              ? t('discovery.noSavedEvents')
              : t('discovery.noRecommendedEvents')}
          </h2>
        </Box>
      ) : (
        <Box className="dsc-events-grid">
          {events.map((event) => {
            const startDate = new Date(event.start_datetime)
            const dateStr = event.all_day
              ? startDate.toLocaleDateString('fr-FR', {
                  weekday: 'short',
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })
              : `${startDate.toLocaleDateString('fr-FR', {
                  month: 'short',
                  day: 'numeric',
                })} ${startDate.toLocaleTimeString('fr-FR', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}`

            return (
              <Box
                key={event.id}
                className="dsc-event-card"
                onClick={() => navigate(`/discovery/events/${event.id}`)}
              >
                {event.image_url && (
                  <Box
                    className="dsc-event-image"
                    style={{ backgroundImage: `url(${event.image_url})` }}
                  />
                )}

                <Box className="dsc-event-content">
                  <h3>{event.canonical_name}</h3>
                  <p className="dsc-event-date">{dateStr}</p>
                  {event.place?.name && (
                    <p className="dsc-event-place">{event.place.name}</p>
                  )}
                  {event.short_description && (
                    <p className="dsc-event-desc">{event.short_description}</p>
                  )}
                  <Box className="dsc-event-footer">
                    {event.category?.label && (
                      <span className="dsc-event-category">{event.category.label}</span>
                    )}
                    {event.pricing_type === 'FREE' && (
                      <span className="dsc-event-free">{t('discovery.free')}</span>
                    )}
                  </Box>
                </Box>
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

export default EventsList
